/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { Box, Stack, Typography } from '@mui/material';
import hljs from 'highlight.js';
import 'highlight.js/styles/github-dark.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { highlightBackground } from '../../styles';
import MarkdownArtifactAction from './MarkdownArtifactAction';
import MarkdownGuardedScrollBox from './MarkdownGuardedScrollBox';

function SyntaxHighlightedCode(
  props: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>,
) {
  const ref = useRef<HTMLElement | null>(null);
  const codeText = String(props.children ?? '');

  useEffect(() => {
    if (ref.current && codeText && props.className?.includes('language-')) {
      ref.current.removeAttribute('data-highlighted');
      hljs.highlightElement(ref.current);
    }
  }, [codeText, props.className]);

  return <code {...props} ref={ref} />;
}

interface MarkdownCodeBlockProps {
  content: string;
  language: string;
  error?: boolean;
  onExpandCode?: (code: { content: string; language: string; title: string }) => void;
  showFullCode?: boolean;
}

function MarkdownCodeBlock({
  content,
  language,
  error = false,
  onExpandCode,
  showFullCode = false,
}: Readonly<MarkdownCodeBlockProps>) {
  const [codeCopied, setCodeCopied] = useState(false);

  const handleCopyCode = useCallback(() => {
    void navigator.clipboard.writeText(content);
    setCodeCopied(true);
  }, [content]);

  return (
    <Stack data-testid="agent-code-artifact" sx={{ my: 1, width: '100%', overflow: 'hidden' }}>
      {!error && (
        <Box
          sx={[
            {
              height: 40,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              border: '1px solid rgba(139, 148, 158, 0.22)',
              borderBottom: 0,
            },
            (theme) => ({
              bgcolor: highlightBackground(theme, { coefficient: 0.5, reverse: true }),
            }),
          ]}
        >
          <Stack
            direction="row"
            sx={{
              mr: 2,
              ml: 2,
              height: 40,
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontSize: 14 }}>{language}</Typography>
            <Box sx={{ flex: 1 }} />
            {onExpandCode && (
              <MarkdownArtifactAction
                ariaLabel="Expand code"
                icon="expand"
                onClick={() =>
                  onExpandCode({ title: `Expanded ${language} code`, content, language })
                }
              >
                Expand
              </MarkdownArtifactAction>
            )}
            <MarkdownArtifactAction
              ariaLabel="Copy code"
              icon={codeCopied ? 'check' : 'copy'}
              onClick={handleCopyCode}
            >
              {codeCopied ? 'Copied' : 'Copy'}
            </MarkdownArtifactAction>
          </Stack>
        </Box>
      )}
      <MarkdownGuardedScrollBox
        contentTestId="agent-code-scroll-region"
        disabled={showFullCode}
        scrollComponent="pre"
        contentSx={{
          fontSize: 14,
          m: 0,
          maxHeight: showFullCode ? undefined : 280,
          width: '100%',
          borderBottomLeftRadius: error ? 8 : 0,
          borderBottomRightRadius: error ? 8 : 0,
          borderTopLeftRadius: error ? 8 : 0,
          borderTopRightRadius: error ? 8 : 0,
          border: '1px solid rgba(139, 148, 158, 0.22)',
          '& code': {
            display: 'block',
            fontSize: 14,
            lineHeight: 1.5,
            whiteSpace: 'pre',
          },
        }}
      >
        <SyntaxHighlightedCode className={`language-${language}`}>{content}</SyntaxHighlightedCode>
      </MarkdownGuardedScrollBox>
    </Stack>
  );
}

export default MarkdownCodeBlock;
